import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, Edit, Delete } from '@mui/icons-material';

import useErrorManager from "../../hooks/useErrorManager";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { manufacturersCode } from '../../constants/manufacturersCode';
import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import GenericDataTable from './GenericDataTable';
import MetersListInfo from './MetersListInformations';



// In a separate module or at the top of your file
const sanitizeEntry = (entry) => {
  // Expected fields:
  // number: int
  // manufacturerId: manufacturer code string (derived from numeric input if needed)
  // identificationNr: int
  // fabricationNr: int
  // deviceType: int
  // deviceVersion: int

  const { number, manufacturerId, identificationNr, fabricationNr, deviceType, deviceVersion } = entry;

  const numNumber = parseInt(number, 10) || null;
  const numDeviceType = parseInt(deviceType, 10);
  const numDeviceVersion = parseInt(deviceVersion, 10);

  const numIdentificationNr = parseInt(identificationNr, 10);
  const numFabricationNr = parseInt(fabricationNr, 10);

  if (
    isNaN(numIdentificationNr) ||
    isNaN(numFabricationNr) ||
    isNaN(numDeviceType) ||
    isNaN(numDeviceVersion)
  ) {
    throw new Error("One or more numeric fields are invalid.");
  }

  // Process manufacturerId
  let manufacturer;
  if (typeof manufacturerId === 'number') {
    manufacturer = manufacturersCode[manufacturerId];
  } else if (typeof manufacturerId === 'string') {
    manufacturer = manufacturerId;
  } else {
    throw new Error("Invalid type for manufacturerId.");
  }

  if (!Object.values(manufacturersCode).includes(manufacturer)) {
    throw new Error("ManufacturerId must be one of the valid manufacturer codes.");
  }

  return {
    number: numNumber,
    manufacturerId: manufacturer,
    identificationNr: numIdentificationNr,
    fabricationNr: numFabricationNr,
    deviceType: numDeviceType,
    deviceVersion: numDeviceVersion,
  };
};



function ManageMetersList() {
  const { gatewayId } = useParams();
  const [metersList, setMetersList] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const { setError, clearError } = useErrorManager();
  const { showNotification } = useNotification();
  const { updateBreadcrumb } = useBreadcrumb();
  const axiosInstance = useAxiosPrivate();

  // Function to fetch the meters list (and update local state)
  const fetchMetersList = useCallback(async () => {
    // setLoading(true);
    try {
      const response = await axiosInstance.get(`/meters/list/${gatewayId}`);
      setMetersList(response.data.list);
      // Extract entries for easier management
      setEntries(response.data.list?.list?.entries || []);
      clearError('fetchMeterList');
      updateBreadcrumb([
        { label: 'Manage Gateways', link: '/gateways' },
        { label: response.data.gw.name || 'Gateway', link: `/gateways/${response.data.gw.id}` },
        { label: 'Meters List', link: null },
      ]);
    } catch (error) {
      setError('fetchMeterList', error.message);
      showNotification('Failed to fetch Meters list', 'error');
    } finally {
      setLoading(false);
    }
  }, [gatewayId]);


  useEffect(() => {
    fetchMetersList();
  }, [fetchMetersList]);



  const handleDataUpdate = async (updatedEntries) => {
    try {
      // Sanitize each entry using the helper function.
      const sanitizedEntries = updatedEntries.map(sanitizeEntry);
  
      // Sort entries by identificationNr in ascending order.
      const sortedEntries = sanitizedEntries.sort((a, b) => a.identificationNr - b.identificationNr);
  
      // Reassign sequential numbers based on sorted order.
      const finalEntries = sortedEntries.map((entry, index) => ({
        ...entry,
        number: index + 1,
      }));
  
      // Generate filename using current date and time.
      const now = new Date();
      // Format as "Updated DD/MM/YYYY, HH:MM"
      const filename = `Updated ${now.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;

      const payload = {
        filename: filename,
        entries: finalEntries,
      };
  
      await axiosInstance.post(`/meters/list/${gatewayId}/json`, payload);
      showNotification('Meters List updated succesfully', 'success');
      // Re-fetch the list after a successful update.
      fetchMetersList();
    } catch (error) {
      setError('updateMetersList', error.message);
      showNotification('Failed to update Meters list: ' + error.message, 'error');
    }
  };
  

  const handleFileImport = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      
      await axiosInstance.post(`/meters/list/${gatewayId}/plt`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      showNotification("PLT file imported successfully", "success");
      fetchMetersList();
    } catch (error) {
      setError("importMetersList", error.message);
      showNotification("Failed to import PLT file: " + error.message, "error");
    }
  };  


  const manufacturerLookup = Object.values(manufacturersCode).reduce((acc, code) => {
    acc[code] = code;
    return acc;
  }, {});

  // // Memoize lookup and columns
  // const visualizedManufacturerLookup = useMemo(() => {
  //   return Object.values(manufacturersCode).reduce((acc, name) => {
  //     acc[name] = name;
  //     return acc;
  //   }, {});
  // }, []);

  const columns = [
    { title: 'Number', field: 'number', editable: 'never' },
    {
      title: 'Manufacturer ID',
      field: 'manufacturerId',
      lookup: manufacturerLookup,
      validate: row =>
        row.manufacturerId && manufacturerLookup[row.manufacturerId]
          ? true
          : 'Please select a valid manufacturer'
    },
    { title: 'Device ID', field: 'identificationNr',
      validate: row => {
        const val = Number(row.identificationNr);
        return val >= 1 && val <= 99999999
          ? true
          : '1 - 99999999';
      }
    },
    { title: 'Fabrication ID', field: 'fabricationNr',
      validate: row => {
        const val = Number(row.fabricationNr);
        return val >= 1 && val <= 99999999
          ? true
          : '1 - 99999999';
      } 
    },
    { title: 'Version', field: 'deviceVersion',
      validate: row => {
        const val = Number(row.deviceVersion);
        return val >= 0 && val <= 255
          ? true
          : '0 - 255';
      }
    },
    { title: 'Device Type', field: 'deviceType',
      validate: row => {
        const val = Number(row.deviceType);
        return val >= 0 && val <= 255
          ? true
          : '0 - 255';
      }
    },
  ];

  if (loading) return <CircularProgress />;

  return (
    <>
      <MetersListInfo
        details={metersList}
      />

      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h7">METERS LIST</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
             <GenericDataTable 
                data={entries} 
                columns={columns}
                onDataUpdate={handleDataUpdate} 
                onFileImport={handleFileImport}
             />  
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default ManageMetersList;
